<template>
  <div>
    <h1>Staging & Simulation</h1>

    <v-tabs v-model="activeTab" background-color="primary" dark>
      <v-tab>Device Simulator</v-tab>
      <v-tab @click="onAlertTabClick">Alert Simulator</v-tab>
      <v-tab @click="onEmailTabClick">Email Simulator</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Device Simulator Tab -->
      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Bulk Device Configuration</v-card-title>
              <v-card-text>
                <label class="text-color-light mb-2">Number of Devices</label>
                <v-text-field
                  v-model.number="deviceCount"
                  type="number"
                  outlined
                  placeholder="50"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2">Device Prefix</label>
                <v-text-field
                  v-model="devicePrefix"
                  outlined
                  placeholder="ximu3-"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2">Starting Number</label>
                <v-text-field
                  v-model.number="startingNumber"
                  type="number"
                  outlined
                  placeholder="1"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2"
                  >Set Torque Range (Nm)</label
                >
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="torqueMin"
                      type="number"
                      outlined
                      label="Min"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model.number="torqueMax"
                      type="number"
                      outlined
                      label="Max"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                  </v-col>
                </v-row>

                <label class="text-color-light my-2"
                  >Device Location Types</label
                >
                <v-textarea
                  v-model="locationTypes"
                  outlined
                  placeholder="Heat Exchanger, Reactor Unit, Blowout Preventer, High Pressure Pipeline, Downhole Room, Pumping Station"
                  hide-details
                  rows="3"
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-textarea>

                <label class="text-color-light mb-2"
                  >Location To Add Devices</label
                >
                <v-select
                  v-model="selectedLocationId"
                  :items="locationItems"
                  item-text="text"
                  item-value="value"
                  outlined
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-select>

                <v-btn
                  @click="generatePreview"
                  color="secondary"
                  dark
                  block
                  class="mt-4"
                  :loading="isGenerating"
                >
                  Generate Preview
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Threshold Configuration</v-card-title>
              <v-card-text>
                <h3 class="text-subtitle-1 mb-2">Default Color Thresholds</h3>
                <v-row>
                  <v-col cols="4">
                    <label class="text-color-light mb-2"
                      >Secure Threshold</label
                    >
                    <v-text-field
                      v-model="thresholds.green"
                      type="text"
                      outlined
                      placeholder="5"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                    <div class="text-caption text-color-light mt-1">
                      Degrees for green color
                    </div>
                  </v-col>
                  <v-col cols="4">
                    <label class="text-color-light mb-2"
                      >Warning Threshold</label
                    >
                    <v-text-field
                      v-model.number="thresholds.yellow"
                      type="number"
                      outlined
                      placeholder="15"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                    <div class="text-caption text-color-light mt-1">
                      Degrees for yellow color
                    </div>
                  </v-col>
                  <v-col cols="4">
                    <label class="text-color-light mb-2"
                      >Critical Threshold</label
                    >
                    <v-text-field
                      v-model.number="thresholds.red"
                      type="number"
                      outlined
                      placeholder="45"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                    <div class="text-caption text-color-light mt-1">
                      Degrees for red color
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Device Preview Table -->
        <v-row v-if="generatedDevices.length > 0" class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex justify-space-between align-center">
                <span
                  >Generated Devices Preview ({{
                    generatedDevices.length
                  }})</span
                >
                <div>
                  <v-btn
                    @click="pushToFirebase"
                    color="primary"
                    dark
                    :loading="isPushing"
                    :disabled="generatedDevices.length === 0"
                    class="mr-2"
                  >
                    Push to Firebase
                  </v-btn>
                  <v-btn
                    @click="clearDevices"
                    color="error"
                    text
                    :disabled="generatedDevices.length === 0"
                  >
                    Clear All
                  </v-btn>
                </div>
              </v-card-title>

              <v-data-table
                :headers="deviceHeaders"
                :items="generatedDevices"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:[`item.deviceId`]="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.deviceId"
                    @save="updateDevice(item)"
                    large
                    persistent
                  >
                    {{ item.deviceId }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.deviceId"
                        label="Edit"
                        single-line
                        counter
                      ></v-text-field>
                    </template>
                  </v-edit-dialog>
                </template>

                <template v-slot:[`item.location`]="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.location"
                    @save="updateDevice(item)"
                    large
                    persistent
                  >
                    {{ item.location }}
                    <template v-slot:input>
                      <v-text-field
                        v-model="item.location"
                        label="Edit"
                        single-line
                        counter
                      ></v-text-field>
                    </template>
                  </v-edit-dialog>
                </template>

                <template v-slot:[`item.setTorque`]="{ item }">
                  <v-edit-dialog
                    :return-value.sync="item.setTorque"
                    @save="updateDevice(item)"
                    large
                    persistent
                  >
                    {{ item.setTorque }}
                    <template v-slot:input>
                      <v-text-field
                        v-model.number="item.setTorque"
                        label="Edit"
                        type="number"
                        single-line
                      ></v-text-field>
                    </template>
                  </v-edit-dialog>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-icon small @click="deleteDevice(item)">
                    mdi-delete
                  </v-icon>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- Status Panel -->
        <v-row v-if="isPushing || pushComplete" class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title>Push Status</v-card-title>
              <v-card-text>
                <v-progress-linear
                  v-if="isPushing"
                  :value="progressPercentage"
                  height="25"
                  color="primary"
                  striped
                >
                  {{ progressText }}
                </v-progress-linear>

                <div v-if="pushComplete" class="mt-2">
                  <p class="success--text">
                    Successfully pushed {{ pushedCount }} devices to Firebase!
                  </p>
                  <p v-if="addedToLocation" class="success--text">
                    All devices were added to location:
                    {{ selectedLocationName }}
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Alert Simulator Tab -->
      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Alert Generator Configuration</v-card-title>
              <v-card-text>
                <label class="text-color-light mb-2">Device ID</label>
                <div v-if="loadingDevices" class="text-center my-4">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                  <div class="mt-2">Loading devices...</div>
                </div>
                <div
                  v-if="!loadingDevices && availableDevices.length === 0"
                  class="text-center red--text mb-4"
                >
                  No devices found. Please create devices first in the Device
                  Simulator tab.
                  <v-btn
                    text
                    color="primary"
                    @click="fetchAvailableDevices"
                    class="mt-2"
                  >
                    <v-icon left>mdi-refresh</v-icon>Refresh Devices
                  </v-btn>
                </div>
                <div
                  v-if="!loadingDevices && availableDevices.length > 0"
                  class="d-flex"
                >
                  <v-select
                    v-model="selectedDeviceForAlerts"
                    :items="availableDevices"
                    item-text="text"
                    item-value="value"
                    outlined
                    placeholder="Select a device"
                    hide-details
                    class="
                      font-size-input
                      border border-radius-md
                      placeholder-lighter
                      text-color-light
                      mb-4
                    "
                  ></v-select>
                  <v-btn
                    icon
                    class="ml-2 mt-2"
                    @click="fetchAvailableDevices"
                    :loading="loadingDevices"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>

                <label class="text-color-light mb-2">Number of Alerts</label>
                <v-text-field
                  v-model.number="alertCount"
                  type="number"
                  outlined
                  placeholder="30"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2">Starting Alert ID</label>
                <v-text-field
                  v-model.number="startingAlertId"
                  type="number"
                  outlined
                  placeholder="0"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2">Alert Start Date</label>
                <v-menu
                  ref="startDateMenu"
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="alertStartDate"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                        mb-4
                      "
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="alertStartDate"
                    :max="maxDate"
                    no-title
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>

                <label class="text-color-light mb-2">Days of History</label>
                <v-text-field
                  v-model.number="alertDaysHistory"
                  type="number"
                  outlined
                  placeholder="30"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2">Percentage Unread</label>
                <v-slider
                  v-model="unreadPercentage"
                  min="0"
                  max="100"
                  thumb-label
                  thumb-size="24"
                  hide-details
                  class="mb-4"
                ></v-slider>

                <label class="text-color-light mb-2"
                  >Severity Distribution</label
                >
                <v-row>
                  <v-col cols="4">
                    <label class="text-color-light">Critical</label>
                    <v-text-field
                      v-model.number="severityDistribution.critical"
                      type="number"
                      outlined
                      suffix="%"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <label class="text-color-light">Warning</label>
                    <v-text-field
                      v-model.number="severityDistribution.warning"
                      type="number"
                      outlined
                      suffix="%"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <label class="text-color-light">Secure</label>
                    <v-text-field
                      v-model.number="severityDistribution.secure"
                      type="number"
                      outlined
                      suffix="%"
                      hide-details
                      class="
                        font-size-input
                        border border-radius-md
                        placeholder-lighter
                        text-color-light
                      "
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-btn
                  @click="generateAlertPreview"
                  color="secondary"
                  dark
                  block
                  class="mt-4"
                  :loading="isGeneratingAlerts"
                  :disabled="!selectedDeviceForAlerts"
                >
                  Generate Alert Preview
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Alert Structure Preview</v-card-title>
              <v-card-text>
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Alert Object Structure
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre class="json-preview">{{ sampleAlertJson }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Alert Preview Table -->
        <v-row v-if="generatedAlerts.length > 0" class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex justify-space-between align-center">
                <span
                  >Generated Alerts Preview ({{ generatedAlerts.length }})</span
                >
                <div>
                  <v-btn
                    @click="pushAlertsToFirebase"
                    color="primary"
                    dark
                    :loading="isPushingAlerts"
                    :disabled="generatedAlerts.length === 0"
                    class="mr-2"
                  >
                    Push to Firebase
                  </v-btn>
                  <v-btn
                    @click="clearAlerts"
                    color="error"
                    text
                    :disabled="generatedAlerts.length === 0"
                  >
                    Clear All
                  </v-btn>
                </div>
              </v-card-title>

              <v-data-table
                :headers="alertHeaders"
                :items="generatedAlerts"
                :items-per-page="10"
                class="elevation-1"
              >
                <template v-slot:[`item.timestamp`]="{ item }">
                  {{ formatDate(item.timestamp) }}
                </template>
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:[`item.read`]="{ item }">
                  <v-icon :color="item.read ? 'green' : 'red'">
                    {{ item.read ? "mdi-check" : "mdi-bell" }}
                  </v-icon>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-icon small @click="deleteAlert(item)"> mdi-delete </v-icon>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- Alert Push Status -->
        <v-row v-if="isPushingAlertits || alertPushComplete" class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title>Push Status</v-card-title>
              <v-card-text>
                <v-progress-linear
                  v-if="isPushingAlerts"
                  :value="alertProgressPercentage"
                  height="25"
                  color="primary"
                  striped
                >
                  {{ alertProgressText }}
                </v-progress-linear>

                <div v-if="alertPushComplete" class="mt-2">
                  <p class="success--text">
                    Successfully pushed {{ pushedAlertCount }} alerts to
                    Firebase!
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Email Simulator Tab -->
      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Email Alert Configuration</v-card-title>
              <v-card-text>
                <label class="text-color-light mb-2">Recipient Email</label>
                <v-text-field
                  v-model="emailRecipient"
                  outlined
                  placeholder="user@example.com"
                  hide-details
                  class="
                    font-size-input
                    border border-radius-md
                    placeholder-lighter
                    text-color-light
                    mb-4
                  "
                ></v-text-field>

                <label class="text-color-light mb-2"
                  >Alert Types to Include</label
                >
                <v-checkbox
                  v-model="includeAlertTypes.critical"
                  label="Critical Alerts"
                  color="red"
                  hide-details
                  class="mt-0"
                ></v-checkbox>
                <v-checkbox
                  v-model="includeAlertTypes.warning"
                  label="Warning Alerts"
                  color="amber"
                  hide-details
                  class="mt-0"
                ></v-checkbox>
                <v-checkbox
                  v-model="includeAlertTypes.secure"
                  label="Secure Alerts"
                  color="green"
                  hide-details
                  class="mt-0 mb-4"
                ></v-checkbox>

                <label class="text-color-light mb-2"
                  >Include Only Unread Alerts</label
                >
                <v-switch
                  v-model="onlyUnreadAlerts"
                  color="primary"
                  hide-details
                  class="mb-4"
                ></v-switch>

                <v-btn
                  @click="generateEmailPreview"
                  color="secondary"
                  dark
                  block
                  class="mt-4"
                  :loading="isGeneratingEmail"
                >
                  Generate Email Preview
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>Email Template Preview</v-card-title>
              <v-card-text>
                <div v-if="!emailHtml" class="text-center">
                  Configure options and generate email preview to see the
                  template.
                </div>
                <div v-if="emailHtml" class="preview-container">
                  <div class="preview-header">Email Preview</div>
                  <div class="preview-content" v-html="emailHtml"></div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Email Controls -->
        <v-row v-if="emailHtml" class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex justify-space-between align-center">
                <span>Email Delivery</span>
                <div>
                  <v-btn
                    @click="sendEmailAlert"
                    color="primary"
                    dark
                    :loading="isSendingEmail"
                    class="mr-2"
                  >
                    Send Email Alert
                  </v-btn>
                  <v-btn @click="copyEmailHtml" color="secondary" outlined>
                    Copy HTML
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <div v-if="emailSent" class="success--text">
                  <v-icon color="success" small class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  <span v-if="emailResult && emailResult.development">
                    Email simulation successful. In production, this would send
                    to {{ emailRecipient }}
                  </span>
                  <span v-else>
                    Email alert sent successfully to {{ emailRecipient }}
                  </span>
                </div>
                <div v-if="htmlCopied" class="success--text">
                  <v-icon color="success" small class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  HTML copied to clipboard
                </div>
                <div v-if="emailError" class="error--text">
                  <v-icon color="error" small class="mr-1"
                    >mdi-alert-circle</v-icon
                  >
                  {{ emailError }}
                </div>
                <div
                  v-if="!process.env.VUE_APP_SENDGRID_API_KEY"
                  class="warning--text mt-2"
                >
                  <v-icon color="warning" small class="mr-1">mdi-alert</v-icon>
                  SendGrid API key not configured. Email will be simulated in
                  development mode.
                </div>
                <div v-if="isDevelopmentMode" class="info--text mt-2">
                  <v-icon color="info" small class="mr-1"
                    >mdi-information</v-icon
                  >
                  Running in development mode. API calls to SendGrid will be
                  simulated.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import {
  postObject,
  updateSingleObjectToFirebase,
  fetchObjects,
  fetchSingleObjectByKey,
} from "@/services/firebase-helpers";
import { DateTime } from "luxon";
import {
  fetchAndFilterAlerts,
  generateAlertEmailHtml,
  sendAlertEmail,
} from "@/services/email-service";

export default {
  name: "StagingView",
  data() {
    return {
      activeTab: 2,

      // Device Simulator data
      deviceCount: 50,
      devicePrefix: "ximu3-",
      startingNumber: 1,
      torqueMin: 120,
      torqueMax: 180,
      locationTypes:
        "Heat Exchanger, Reactor Unit, Blowout Preventer, High Pressure Pipeline, Downhole Room, Pumping Station, Cooling Tower, Pressure Vessel, Flare Stack, Cracking Unit, Refinery Column, Storage Tank Flange",
      thresholds: {
        green: "5",
        yellow: 15,
        red: 45,
      },
      isGenerating: false,
      isPushing: false,
      pushComplete: false,
      progressCount: 0,
      pushedCount: 0,
      generatedDevices: [],
      selectedLocationId: null,
      addedToLocation: false,
      deviceHeaders: [
        { text: "Device Key", value: "key" },
        { text: "Device ID", value: "deviceId" },
        { text: "Location", value: "location" },
        { text: "Set Torque", value: "setTorque" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],

      // Alert Simulator data
      selectedDeviceForAlerts: null,
      deviceDetails: null,
      availableDevices: [],
      loadingDevices: false,
      alertCount: 5,
      startingAlertId: 0,
      alertDaysHistory: 30,
      alertStartDate:( new Date()).toISOString().substr(0, 10), // Today's date in YYYY-MM-DD format
      startDateMenu: false,
      unreadPercentage: 100,
      severityDistribution: {
        critical: 100,
        warning: 0,
        secure: 0,
      },
      isGeneratingAlerts: false,
      isPushingAlerts: false,
      alertPushComplete: false,
      alertProgressCount: 0,
      pushedAlertCount: 0,
      generatedAlerts: [],
      alertHeaders: [
        { text: "ID", value: "id", width: "10%" },
        { text: "Timestamp", value: "timestamp", width: "15%" },
        { text: "Status", value: "status", width: "15%" },
        {
          text: "Torque Angle Deviation",
          value: "torqueAngleDeviation",
          width: "15%",
        },
        { text: "Read Status", value: "read", width: "10%" },
        { text: "Device ID", value: "deviceId", width: "15%" },
        { text: "Device Location", value: "deviceLocation", width: "15%" },
        { text: "Actions", value: "actions", sortable: false, width: "5%" },
      ],

      // Email Simulator data
      emailRecipient: "pbrown101393@gmail.com",
      includeAlertTypes: {
        critical: true,
        warning: true,
        secure: false,
      },
      onlyUnreadAlerts: true,
      isGeneratingEmail: false,
      isSendingEmail: false,
      emailHtml: null,
      emailSent: false,
      htmlCopied: false,
      emailError: null,
      filteredAlerts: [],
      emailResult: null,
      isDevelopmentMode:
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1",
    };
  },
  computed: {
    // Device Simulator computed properties
    locations() {
      return this.$store.state.locations || [];
    },
    locationItems() {
      return this.locations.map((location) => ({
        text: `${location.name} (${location.city}, ${location.state})`,
        value: location.key,
      }));
    },
    selectedLocation() {
      if (!this.selectedLocationId) return null;
      return this.locations.find((loc) => loc.key === this.selectedLocationId);
    },
    selectedLocationName() {
      return this.selectedLocation ? this.selectedLocation.name : "";
    },
    progressPercentage() {
      const total = this.isPushing
        ? this.generatedDevices.length
        : this.deviceCount;
      const count = this.isPushing ? this.progressCount : this.progressCount;
      return (count / total) * 100;
    },
    progressText() {
      if (this.isPushing) {
        return `Pushed ${this.progressCount} of ${this.generatedDevices.length} devices`;
      }
      return `Generated ${this.progressCount} of ${this.deviceCount} devices`;
    },

    // Alert Simulator computed properties
    alertProgressPercentage() {
      const total = this.isPushingAlerts
        ? this.generatedAlerts.length
        : this.alertCount;
      const count = this.isPushingAlerts
        ? this.alertProgressCount
        : this.alertProgressCount;
      return (count / total) * 100;
    },
    alertProgressText() {
      if (this.isPushingAlerts) {
        return `Pushed ${this.alertProgressCount} of ${this.generatedAlerts.length} alerts`;
      }
      return `Generated ${this.alertProgressCount} of ${this.alertCount} alerts`;
    },
    sampleAlertJson() {
      if (this.generatedAlerts.length > 0) {
        return JSON.stringify(this.generatedAlerts[0], null, 2);
      }
      return JSON.stringify(
        {
          id: 0,
          key: "0",
          deviceId: "ximu3-001",
          deviceLocation: "Heat Exchanger, Sector 5",
          timestamp: new Date().toISOString(),
          status: "Warning",
          initialTorque: 120,
          currentTorque: 110,
          initialAngle: 0,
          torqueAngleDeviation: 15,
          looseningRate: "-0.7",
          torqueDeviation: -10,
          batteryLevel: 82,
          temperature: 75,
          riskMessage:
            "Fastener loosening at a rate of 0.7 Nm per degree. Inspection recommended.",
          read: false,
          readTimestamp: null,
        },
        null,
        2
      );
    },

    // Maximum date for the date picker (today)
    maxDate() {
      return new Date().toISOString().substr(0, 10);
    },

    // Email Simulator computed properties
    process() {
      return process;
    },

    isDevelopmentMode() {
      return (
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      );
    },
  },
  mounted() {
    // Fetch locations if they're not already loaded
    if (!this.locations.length) {
      this.$store.dispatch("handleUserLocations", this.$store.state.auth.uid);
    }

    // Fetch available devices for alert simulation
    this.fetchAvailableDevices();
  },
  methods: {
    // Device Simulator methods
    generatePreview() {
      if (this.deviceCount <= 0) {
        alert("Please enter a valid number of devices to generate");
        return;
      }

      try {
        this.isGenerating = true;
        this.progressCount = 0;
        this.generatedDevices = [];
        this.addedToLocation = false;
        this.pushComplete = false;

        // Parse location types
        const locationTypeArray = this.locationTypes
          .split(",")
          .map((type) => type.trim())
          .filter((type) => type);

        // Generate devices locally
        for (let i = 0; i < this.deviceCount; i++) {
          // Create a unique device key and ID with starting number
          const deviceNumber = this.startingNumber + i;
          const deviceKey = `${this.devicePrefix}${String(
            deviceNumber
          ).padStart(3, "0")}`;
          const deviceId = deviceKey;

          // Randomly select a location type and add a sector
          const locationType =
            locationTypeArray[
              Math.floor(Math.random() * locationTypeArray.length)
            ];
          const sectorNumber = Math.floor(Math.random() * 20) + 1;
          const deviceLocation = `${locationType}, Sector ${sectorNumber}`;

          // Generate a random torque within the specified range
          const setTorque =
            Math.floor(Math.random() * (this.torqueMax - this.torqueMin + 1)) +
            this.torqueMin;

          // Create the device data object (matches Firebase format)
          const deviceData = {
            key: deviceKey,
            deviceId: deviceId,
            location: deviceLocation,
            setTorque: setTorque,
            thresholds: {
              green: this.thresholds.green,
              yellow: this.thresholds.yellow,
              red: this.thresholds.red,
            },
            lastUpdated: new Date().toISOString(),
          };

          // Add device to our local array
          this.generatedDevices.push(deviceData);

          // Update progress
          this.progressCount++;
        }
      } catch (error) {
        console.error("Error generating device preview:", error);
        alert("Error: " + error.message);
      } finally {
        this.isGenerating = false;
      }
    },

    async pushToFirebase() {
      if (this.generatedDevices.length === 0) {
        alert("No devices to push. Please generate devices first.");
        return;
      }

      try {
        this.isPushing = true;
        this.progressCount = 0;
        this.pushedCount = 0;
        this.addedToLocation = false;

        // Push each device to Firebase
        for (const device of this.generatedDevices) {
          // Create the device data object for Firebase
          const deviceData = {
            deviceId: device.deviceId,
            location: device.location,
            setTorque: device.setTorque,
            thresholds: { ...device.thresholds },
            lastUpdated: new Date().toISOString(),
          };

          // Post the device to Firebase
          await postObject(deviceData, "devices", device.key);

          // Update progress
          this.progressCount++;
          this.pushedCount++;
        }

        // If a location is selected, add all devices to it
        if (this.selectedLocationId) {
          await this.addDevicesToLocation();
          this.addedToLocation = true;
        }

        this.pushComplete = true;
        alert(`Successfully pushed ${this.pushedCount} devices to Firebase!`);

        // Refresh available devices for the alert simulator
        this.fetchAvailableDevices();
      } catch (error) {
        console.error("Error pushing devices to Firebase:", error);
        alert("Error: " + error.message);
      } finally {
        this.isPushing = false;
      }
    },

    async addDevicesToLocation() {
      if (!this.selectedLocationId || this.generatedDevices.length === 0)
        return;

      try {
        // Get all locations and filter to find the selected one
        const allLocations = await fetchObjects("location");
        const locationData = allLocations.find(
          (location) => location.key === this.selectedLocationId
        );

        if (locationData) {
          // Create or update the devices array
          const updatedDevices = locationData.devices || [];

          // Add all generated device IDs if they're not already in the array
          for (const device of this.generatedDevices) {
            if (!updatedDevices.includes(device.key)) {
              updatedDevices.push(device.key);
            }
          }

          // Update the location with the new devices array
          await updateSingleObjectToFirebase(
            { ...locationData, devices: updatedDevices },
            "location",
            this.selectedLocationId
          );

          console.log(
            `Added ${this.generatedDevices.length} devices to location ${this.selectedLocationId}`
          );
        } else {
          throw new Error(
            "Location not found! Please check the selected location."
          );
        }
      } catch (error) {
        console.error("Error adding devices to location:", error);
        throw error;
      }
    },

    updateDevice(device) {
      // The v-edit-dialog automatically updates the item in the array
      console.log(`Updated device: ${device.key}`);
    },

    deleteDevice(device) {
      const index = this.generatedDevices.findIndex(
        (d) => d.key === device.key
      );
      if (index !== -1) {
        this.generatedDevices.splice(index, 1);
        console.log(`Deleted device: ${device.key}`);
      }
    },

    clearDevices() {
      if (confirm("Are you sure you want to clear all devices?")) {
        this.generatedDevices = [];
        this.pushComplete = false;
      }
    },

    // Alert Simulator methods
    async fetchAvailableDevices() {
      try {
        this.loadingDevices = true;
        this.availableDevices = [];

        console.log("Fetching available devices...");
        const devices = await fetchObjects("devices");
        console.log("Devices fetched:", devices);

        if (devices && devices.length > 0) {
          // Process devices with better key handling
          this.availableDevices = devices
            .filter((device) => device && (device.deviceId || device.key)) // Filter out invalid devices
            .map((device) => {
              // The key used for Firebase operations
              const deviceKey = device.key || device.deviceId;

              // The display text that shows in the dropdown
              const displayId = device.deviceId || device.key || "Unknown";
              const location = device.location || "No location";

              return {
                text: `${displayId} (${location})`,
                value: deviceKey,
              };
            });

          console.log("Available devices processed:", this.availableDevices);
        } else {
          console.log("No devices found");
          this.availableDevices = [];
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        this.availableDevices = [];
      } finally {
        this.loadingDevices = false;
      }
    },

    async fetchDeviceDetails(deviceKey) {
      try {
        console.log(`Fetching details for device with key: ${deviceKey}`);
        const deviceDetails = await fetchSingleObjectByKey(
          "devices",
          deviceKey
        );
        console.log("Device details:", deviceDetails);

        if (!deviceDetails) {
          console.error(`No device found with key: ${deviceKey}`);
          return null;
        }

        return {
          ...deviceDetails,
          key: deviceKey, // Ensure the key is included in the details
        };
      } catch (error) {
        console.error("Error fetching device details:", error);
        return null;
      }
    },

    async generateAlertPreview() {
      if (!this.selectedDeviceForAlerts) {
        alert("Please select a device to generate alerts for");
        return;
      }

      try {
        this.isGeneratingAlerts = true;
        this.alertProgressCount = 0;
        this.generatedAlerts = [];
        this.alertPushComplete = false;

        console.log(
          `Generating alerts for device: ${this.selectedDeviceForAlerts}`
        );

        // Fetch device details
        const deviceDetails = await this.fetchDeviceDetails(
          this.selectedDeviceForAlerts
        );
        if (!deviceDetails) {
          throw new Error(
            `Device details not found for key: ${this.selectedDeviceForAlerts}`
          );
        }

        this.deviceDetails = deviceDetails;
        console.log("Using device details:", deviceDetails);

        // Get the actual device ID (either from deviceId field or key)
        const deviceId = deviceDetails.deviceId || this.selectedDeviceForAlerts;

        // Generate alerts
        const now = DateTime.now();

        // Use the selected start date if provided, otherwise fallback to calculated date
        let startDate = this.alertStartDate
          ? DateTime.fromISO(this.alertStartDate)
          : now.minus({ days: this.alertDaysHistory });

        // If the start date plus days of history would go beyond current date,
        // just use the time between start date and now
        const availableDays = Math.min(
          this.alertDaysHistory,
          now.diff(startDate, "days").days
        );

        // Current date starts at the selected start date
        let currentDate = startDate;

        // Calculate the total time span in hours
        const totalHoursSpan = availableDays * 24;

        // Calculate average hours between alerts (with some variance)
        const avgHoursBetweenAlerts = totalHoursSpan / this.alertCount;

        // Create the specified number of alerts
        for (let i = 0; i < this.alertCount; i++) {
          // Calculate alert ID with starting index
          const alertId = this.startingAlertId + i;

          // Determine status based on distribution
          const randomValue = Math.random() * 100;
          let status;

          if (randomValue < this.severityDistribution.critical) {
            status = "Critical";
          } else if (
            randomValue <
            this.severityDistribution.critical +
              this.severityDistribution.warning
          ) {
            status = "Warning";
          } else {
            status = "Secure";
          }

          // Calculate torque angle deviation based on status
          let torqueAngleDeviation;
          if (status === "Critical") {
            // Critical: 30+ degrees
            torqueAngleDeviation = Math.floor(Math.random() * 30) + 30;
          } else if (status === "Warning") {
            // Warning: 9-29 degrees
            torqueAngleDeviation = Math.floor(Math.random() * 21) + 9;
          } else {
            // Secure: 0-8 degrees
            torqueAngleDeviation = Math.floor(Math.random() * 8);
          }

          // Calculate torque deviation (10% of initial torque for critical, less for others)
          const initialTorque = deviceDetails.setTorque || 150;
          let torqueDeviation;
          if (status === "Critical") {
            torqueDeviation = -(initialTorque * 0.1 + Math.random() * 5);
          } else if (status === "Warning") {
            torqueDeviation = -(initialTorque * 0.05 + Math.random() * 3);
          } else {
            torqueDeviation = -(Math.random() * 2);
          }

          // Current torque
          const currentTorque = initialTorque + torqueDeviation;

          // Loosening rate
          const looseningRate =
            torqueAngleDeviation > 0
              ? (torqueDeviation / torqueAngleDeviation).toFixed(1)
              : "0.0";

          // Battery level decreases slowly over time
          const batteryLevel = Math.max(
            50,
            95 - Math.floor((i / this.alertCount) * 45)
          );

          // Temperature varies a bit
          const temperature = 70 + Math.floor(Math.random() * 10);

          // Risk message
          let riskMessage;
          if (status === "Critical") {
            riskMessage = `Fastener loosening at a rate of ${Math.abs(
              parseFloat(looseningRate)
            )} Nm per degree. Immediate inspection recommended.`;
          } else if (status === "Warning") {
            riskMessage = `Fastener loosening at a rate of ${Math.abs(
              parseFloat(looseningRate)
            )} Nm per degree. Inspection recommended.`;
          } else {
            riskMessage =
              torqueAngleDeviation === 0
                ? "Fastener secure with no loosening detected."
                : "Fastener secure with minimal loosening.";
          }

          // Determine read status
          const isRead = Math.random() * 100 > this.unreadPercentage;

          // Create alert object
          const alert = {
            id: alertId,
            key: alertId.toString(),
            deviceId: deviceId,
            deviceLocation: deviceDetails.location || "Unknown location",
            timestamp: currentDate.toISO(),
            status: status,
            initialTorque: initialTorque,
            currentTorque: currentTorque,
            initialAngle: 0,
            torqueAngleDeviation: torqueAngleDeviation,
            looseningRate: looseningRate,
            torqueDeviation: torqueDeviation,
            batteryLevel: batteryLevel,
            temperature: temperature,
            riskMessage: riskMessage,
            read: isRead,
            readTimestamp: isRead
              ? currentDate
                  .plus({ minutes: Math.floor(Math.random() * 60) })
                  .toISO()
              : null,
          };

          // Add to alerts array
          this.generatedAlerts.push(alert);

          // Move forward by a random amount of time, but ensure we don't go beyond the current date
          // Calculate a random number of hours to add, with some variance around the average
          const hoursToAdd = Math.max(
            1,
            avgHoursBetweenAlerts * (0.5 + Math.random())
          );

          currentDate = currentDate.plus({ hours: hoursToAdd });

          // If we've gone beyond the current date/time, stop generating more alerts
          if (currentDate > now) {
            console.log(
              `Stopped alert generation at ${
                i + 1
              } alerts - reached current date/time`
            );
            break;
          }

          // Update progress
          this.alertProgressCount++;
        }

        // Sort alerts by timestamp (newest first)
        this.generatedAlerts.sort(
          (a, b) =>
            DateTime.fromISO(b.timestamp).toMillis() -
            DateTime.fromISO(a.timestamp).toMillis()
        );
      } catch (error) {
        console.error("Error generating alerts:", error);
        alert("Error: " + error.message);
      } finally {
        this.isGeneratingAlerts = false;
      }
    },

    async pushAlertsToFirebase() {
      if (this.generatedAlerts.length === 0) {
        alert("No alerts to push. Please generate alerts first.");
        return;
      }

      try {
        this.isPushingAlerts = true;
        this.alertProgressCount = 0;
        this.pushedAlertCount = 0;

        // Push each alert to Firebase under the alerts collection
        for (const alert of this.generatedAlerts) {
          // Post the alert to Firebase using just the ID as the key
          await postObject(alert, "alerts", alert.key);

          // Update progress
          this.alertProgressCount++;
          this.pushedAlertCount++;
        }

        this.alertPushComplete = true;
        alert(
          `Successfully pushed ${this.pushedAlertCount} alerts to Firebase!`
        );
      } catch (error) {
        console.error("Error pushing alerts to Firebase:", error);
        alert("Error: " + error.message);
      } finally {
        this.isPushingAlerts = false;
      }
    },

    deleteAlert(alert) {
      const index = this.generatedAlerts.findIndex((a) => a.id === alert.id);
      if (index !== -1) {
        this.generatedAlerts.splice(index, 1);
        console.log(`Deleted alert: ${alert.id}`);
      }
    },

    clearAlerts() {
      if (confirm("Are you sure you want to clear all alerts?")) {
        this.generatedAlerts = [];
        this.alertPushComplete = false;
      }
    },

    // Utility methods
    formatDate(dateString) {
      return DateTime.fromISO(dateString).toFormat("MMM d, yyyy HH:mm");
    },

    getStatusColor(status) {
      switch (status) {
        case "Critical":
          return "red";
        case "Warning":
          return "amber";
        case "Secure":
          return "green";
        default:
          return "grey";
      }
    },

    onAlertTabClick() {
      // Refresh the device list when switching to alert tab
      this.fetchAvailableDevices();
    },

    // Email Simulator methods
    onEmailTabClick() {
      // Reset email state when switching to tab
      this.emailSent = false;
      this.htmlCopied = false;
      this.emailError = null;
    },

    async generateEmailPreview() {
      if (!this.emailRecipient) {
        this.emailError = "Please enter a recipient email address";
        return;
      }

      this.emailSent = false;
      this.htmlCopied = false;
      this.emailError = null;

      try {
        this.isGeneratingEmail = true;

        // Fetch and filter alerts using the service
        this.filteredAlerts = await fetchAndFilterAlerts({
          includeAlertTypes: this.includeAlertTypes,
          onlyUnreadAlerts: this.onlyUnreadAlerts,
        });

        // Generate email HTML template using the service
        this.emailHtml = generateAlertEmailHtml(this.filteredAlerts, {
          onlyUnreadAlerts: this.onlyUnreadAlerts,
        });
      } catch (error) {
        console.error("Error generating email preview:", error);
        this.emailError = "Failed to generate email preview: " + error.message;
      } finally {
        this.isGeneratingEmail = false;
      }
    },

    copyEmailHtml() {
      if (!this.emailHtml) return;

      // Create temporary textarea element to copy HTML
      const textarea = document.createElement("textarea");
      textarea.value = this.emailHtml;
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand("copy");
        this.htmlCopied = true;
        this.emailSent = false;
        this.emailError = null;

        // Hide "copied" message after 3 seconds
        setTimeout(() => {
          this.htmlCopied = false;
        }, 3000);
      } catch (err) {
        console.error("Failed to copy HTML: ", err);
        this.emailError = "Failed to copy HTML to clipboard";
      } finally {
        document.body.removeChild(textarea);
      }
    },

    async sendEmailAlert() {
      if (!this.emailRecipient) {
        this.emailError = "Please enter a recipient email address";
        return;
      }

      this.isSendingEmail = true;
      this.emailSent = false;
      this.htmlCopied = false;
      this.emailError = null;

      try {
        // Use the email service to send the alert email
        this.emailResult = await sendAlertEmail(this.emailRecipient, {
          includeAlertTypes: this.includeAlertTypes,
          onlyUnreadAlerts: this.onlyUnreadAlerts,
        });

        if (this.emailResult.success) {
          this.emailSent = true;

          // Hide "sent" message after 5 seconds
          setTimeout(() => {
            this.emailSent = false;
          }, 5000);
        } else {
          this.emailError = this.emailResult.error || "Failed to send email";
        }
      } catch (error) {
        console.error("Error sending email alert:", error);
        this.emailError = "Failed to send email: " + error.message;
      } finally {
        this.isSendingEmail = false;
      }
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 0.8rem;
}

.json-preview {
  background-color: #f5f5f5;
  padding: 10px;
  font-family: monospace;
  font-size: 0.8rem;
  overflow: auto;
  max-height: 300px;
  border-radius: 4px;
}

.preview-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.preview-header {
  background-color: #f5f5f5;
  padding: 10px;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
}

.preview-content {
  padding: 10px;
  max-height: 500px;
  overflow: auto;
  background-color: white;
}
</style>
